import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import Accordion from "@/components/base/Accordion";
import Eyebrow from "@/components/base/Eyebrow";
import MicroLabel from "@/components/base/MicroLabel";
import Callout from "@/components/base/Callout";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import BrokerStrip from "@/components/feature/BrokerStrip";
import PricePathChart from "@/components/feature/PricePathChart";
import { orderTypes } from "@/mocks/orderTypes";
import type { BrokerRationaleKey } from "@/mocks/brokers";

interface OrderTypeLayoutProps {
  slug: string;
}

export default function OrderTypeLayout({ slug }: OrderTypeLayoutProps) {
  const type = orderTypes.find((item) => item.slug === slug);

  if (!type) {
    return (
      <section className="px-4 md:px-8 pt-40 pb-24 text-center">
        <h1 className="font-heading font-black uppercase text-2xl text-foreground-950">
          Order type not found
        </h1>
        <Link
          to="/"
          className="mt-6 inline-block font-label text-xs uppercase tracking-[0.16em] font-semibold text-foreground-950 border-b-2 border-foreground-950 cursor-pointer"
        >
          Back to home
        </Link>
      </section>
    );
  }

  const related = type.related
    .map((relatedSlug) => orderTypes.find((item) => item.slug === relatedSlug))
    .filter((item): item is (typeof orderTypes)[number] => Boolean(item));

  const siblings = orderTypes.filter((item) => item.slug !== slug);

  /* Only stop and stop-limit orders actually "fire" (trigger). Market and limit
     orders fill, so the chart copy must match each type's own mechanism. */
  const isTriggerType = slug === "stop-orders" || slug === "stop-limit-orders";
  const chartTitleVerb = isTriggerType ? "fires" : "fills";
  const chartDescPhrase =
    slug === "stop-limit-orders"
      ? "triggers and either fills or misses"
      : isTriggerType
        ? "triggers and fills"
        : "fills";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: type.faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  return (
    <>
      <PageHero eyebrow={`Order type · ${type.category}`} title={type.name} description={type.tagline}>
        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-foreground-950 font-label text-[11px] uppercase tracking-[0.14em] font-semibold text-foreground-950 hover:bg-foreground-950 hover:text-primary-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            All order types
          </Link>
          {related.map((item) => (
            <Link
              key={item.slug}
              to={`/${item.slug}`}
              className="inline-flex items-center px-4 py-2.5 border-2 border-foreground-950 bg-background-100 font-label text-[11px] uppercase tracking-[0.14em] font-semibold text-foreground-950 hover:bg-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              {item.name}
            </Link>
          ))}
        </div>
      </PageHero>

      {/* Definition + key points */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Definition</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-4xl">
              What it is
            </h2>
            <p className="mt-6 text-base leading-relaxed text-foreground-600">{type.definition}</p>
            <p className="mt-6 text-base leading-relaxed text-foreground-600">{type.intro}</p>
            <div className="mt-8 border-t-2 border-foreground-950 pt-5">
              <Eyebrow tone="muted">In one sentence</Eyebrow>
              <p className="mt-3 font-heading font-bold text-lg md:text-xl leading-snug text-foreground-950">
                {type.oneLiner}
              </p>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-5">
            <div className="border-2 border-primary-500 bg-foreground-950 p-6 md:p-7">
              <Eyebrow tone="onDarkPrimary">What matters most</Eyebrow>
              <div className="mt-5 divide-y divide-primary-500/20 border-t-2 border-primary-500/30">
                {type.keyPoints.map((point) => (
                  <div key={point.title} className="flex items-start gap-4 py-5">
                    <span className="mt-1.5 w-2 h-2 shrink-0 bg-primary-500" aria-hidden="true"></span>
                    <div>
                      <p className="font-heading font-bold uppercase tracking-tight text-sm text-background-50">
                        {point.title}
                      </p>
                      <p className="mt-1.5 text-sm leading-relaxed text-background-300">
                        {point.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it works + worked example */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          <Reveal className="flex flex-col">
            <Eyebrow>Mechanics</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-4xl">
              How it works
            </h2>
            <div className="mt-8 border-t-2 border-foreground-950">
              {type.howItWorks.map((paragraph, index) => (
                <div key={index} className="flex items-start gap-5 py-5 border-b-2 border-foreground-950">
                  <span className="shrink-0 font-heading font-black text-3xl leading-none text-stroke text-foreground-950" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm md:text-base leading-relaxed text-foreground-600">{paragraph}</p>
                </div>
              ))}
            </div>
            <div className="mt-auto pt-6 lg:pt-8 flex items-start gap-3">
              <i className="ri-arrow-down-line lg:hidden text-foreground-950 text-lg mt-0.5"></i>
              <i className="ri-arrow-right-line hidden lg:block text-foreground-950 text-lg mt-0.5"></i>
              <p className="text-sm leading-relaxed text-foreground-600">
                <span className="lg:hidden">The card below runs steps 01–03 on a single trade, start to finish.</span>
                <span className="hidden lg:inline">The card alongside runs steps 01–03 on a single trade, start to finish.</span>
              </p>
            </div>
          </Reveal>

          {/* Worked example */}
          <Reveal delay={120} className="flex">
            <div className="w-full flex flex-col border-2 border-foreground-950 bg-background-50">
              <div className="px-5 py-4 bg-foreground-950 flex items-center justify-between border-b-2 border-foreground-950">
                <MicroLabel tone="onDarkPrimary">Worked example</MicroLabel>
                <MicroLabel tone="onDarkMuted">Steps 01–03</MicroLabel>
              </div>
              <div className="p-5 md:p-6 flex flex-col flex-1">
                <p className="text-sm leading-relaxed text-foreground-600">{type.example.scenario}</p>
                <div className="mt-5 mb-4 border-2 border-foreground-950 divide-y-2 divide-foreground-950">
                  {type.example.table.map((row) => (
                    <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3">
                      <span className="text-sm text-foreground-600">{row.label}</span>
                      <span className="font-label text-xs md:text-sm font-semibold text-foreground-950 text-right">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
                <Callout tone="accent" mark="arrow" label="The takeaway" className="mt-auto">
                  {type.example.note}
                </Callout>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Where it fires on the chart */}
      <PricePathChart
        defaultKey={slug.replace("-orders", "") as "market" | "limit" | "stop" | "stop-limit"}
        eyebrow="On the chart"
        title={`Where the ${type.name.toLowerCase()} ${chartTitleVerb}`}
        description={`See the exact point on the price path where a ${type.name.toLowerCase()} ${chartDescPhrase} — then switch tabs to compare it with the other three.`}
      />

      {/* Pros and cons */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <Eyebrow>Trade-offs</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-4xl">
              The upside and the risk
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Reveal>
            <div className="h-full flex flex-col border-2 border-foreground-950 bg-primary-100 p-6 md:p-8">
              <div className="flex flex-col gap-2 pb-4 border-b-2 border-foreground-950">
                <MicroLabel mark="plus" tone="muted">Upside</MicroLabel>
                <h3 className="font-heading font-black uppercase tracking-tight text-xl text-foreground-950">
                  Where it shines
                </h3>
              </div>
              <ul className="flex-1 flex flex-col">
                {type.pros.map((item) => (
                  <li
                    key={item}
                    className="flex-1 flex items-start gap-3 py-4 border-b-2 border-foreground-950 text-sm leading-relaxed text-foreground-800"
                  >
                    <span className="w-3.5 shrink-0 text-center font-heading font-black text-[13px] leading-none text-foreground-950 mt-1" aria-hidden="true">
                      +
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full flex flex-col border-2 border-foreground-950 bg-accent-100 p-6 md:p-8">
              <div className="flex flex-col gap-2 pb-4 border-b-2 border-foreground-950">
                <MicroLabel mark="minus" tone="muted">Risk</MicroLabel>
                <h3 className="font-heading font-black uppercase tracking-tight text-xl text-foreground-950">
                  What to watch
                </h3>
              </div>
              <ul className="flex-1 flex flex-col">
                {type.cons.map((item) => (
                  <li
                    key={item}
                    className="flex-1 flex items-start gap-3 py-4 border-b-2 border-foreground-950 text-sm leading-relaxed text-foreground-800"
                  >
                    <span className="w-3.5 shrink-0 text-center font-heading font-black text-[13px] leading-none text-accent-800 mt-1" aria-hidden="true">
                      −
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          </div>
        </div>
      </section>

      <BrokerStrip
        label={`Where to trade a ${type.name.toLowerCase()}`}
        heading="When you are ready to place one on a live account"
        note="This order type behaves the same at every broker — pricing and platforms differ."
        rationaleKey={slug as BrokerRationaleKey}
      />

      {/* Common misuses */}
      {type.misuses.length > 0 && (
        <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <Eyebrow>Common misuses</Eyebrow>
              <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-4xl">
                Where beginners go wrong
              </h2>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">
                The {type.name.toLowerCase()} is not the problem on its own — how it gets used is. These are the habits that trip up new traders, and what to do instead.
              </p>
            </div>

            <div className="mt-10 border-t-2 border-foreground-950">
              {type.misuses.map((item, index) => (
                <Reveal key={item.title}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-start py-7 border-b-2 border-foreground-950">
                    <div className="hidden lg:flex lg:col-span-1 items-start justify-end">
                      <span className="font-heading font-black text-3xl leading-none text-stroke text-foreground-950" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="lg:col-span-6">
                      <div className="flex items-start gap-3">
                        <span className="lg:hidden shrink-0 font-heading font-black text-2xl leading-none text-stroke text-foreground-950 mt-0.5" aria-hidden="true">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-heading font-bold uppercase tracking-tight text-base md:text-xl leading-snug text-foreground-950">
                            {item.title}
                          </h3>
                          <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                            {item.why}
                          </p>
                          <p className="mt-4 border-l-2 border-accent-500 pl-3 text-sm leading-relaxed italic text-foreground-700">
                            {item.example}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="lg:col-span-5">
                      <Callout mark="arrow" label="Do this instead">
                        {item.instead}
                      </Callout>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* When to use */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-foreground-950">
        <div className="max-w-7xl mx-auto">
          <Eyebrow tone="onDark">In practice</Eyebrow>
          <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-background-50 text-3xl md:text-4xl max-w-2xl">
            When to use a {type.name.toLowerCase()}
          </h2>
          <p className="mt-5 text-sm md:text-base leading-relaxed text-background-300 max-w-2xl">
            These three are the situations where a {type.name.toLowerCase()} does exactly what you need — reach for it whenever one of them sounds like the trade in front of you.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {type.whenToUse.map((item, index) => (
              <Reveal key={item} delay={index * 90}>
                <div className="h-full border-2 border-primary-500 p-6">
                  <span className="font-heading font-black text-5xl leading-none text-stroke text-primary-500" aria-hidden="true">
                    0{index + 1}
                  </span>
                  <p className="mt-5 text-sm md:text-base leading-relaxed text-background-200">
                    {item}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 border-t-2 border-background-50/15 pt-12">
            <Eyebrow tone="onDark">The other side</Eyebrow>
            <h3 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-background-50 text-2xl md:text-3xl max-w-2xl">
              When not to use a {type.name.toLowerCase()}
            </h3>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-background-300 max-w-2xl">
              The mirror image: these are the situations where the {type.name.toLowerCase()} is the
              wrong tool — reaching for a different order is the better call.
            </p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
              {type.whenNotToUse.map((item, index) => (
                <Reveal key={item} delay={index * 90}>
                  <div className="h-full border-2 border-accent-500 p-6">
                    <p className="text-sm md:text-base leading-relaxed text-background-200">
                      {item}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {type.faqs.length > 0 && (
        <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <Eyebrow>FAQ</Eyebrow>
              <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-4xl">
                Questions, answered
              </h2>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">
                The questions traders ask most about the {type.name.toLowerCase()} — answered plainly.
              </p>
              <Link
                to="/glossary"
                className="mt-8 inline-flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground-950 border-b-2 border-foreground-950 pb-1 hover:text-accent-600 hover:border-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse the full glossary
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="lg:col-span-8">
              <Accordion items={type.faqs} />
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {siblings.length > 0 && (
        <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Eyebrow>Keep going</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase tracking-tight text-3xl md:text-4xl text-foreground-950">
              The other order types
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {siblings.map((item, index) => (
                <Reveal key={item.slug} delay={index * 90} className="h-full">
                  <Link
                    to={`/${item.slug}`}
                    className="group flex h-full items-start gap-4 border-2 border-foreground-950 bg-background-50 p-5 transition-colors hover:bg-foreground-950 cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading font-bold uppercase tracking-tight text-base text-foreground-950 group-hover:text-background-50">
                        {item.name}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600 group-hover:text-background-300">
                        {item.summary}
                      </p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-950 group-hover:text-primary-500 ml-auto mt-1 transition-transform group-hover:translate-x-1"></i>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand
        title={type.cta.title}
        description={type.cta.description}
        to="/order-execution"
        buttonLabel={type.cta.buttonLabel}
      />
    </>
  );
}